/**
 * identity-svc passkey management — the SIGNED-IN half (V072, PR 3).
 *
 * The anonymous half (request a device link, then register with it) lives in
 * the web app's own `passkeyApi`, which deliberately sends no Authorization
 * header. These three need a live session, so they ride the shared ApiClient.
 */

import type { Uuid } from "@re-platform/types";

import type { ApiClient } from "../client";

/** One registered credential, for the profile's "Manage passkeys" list. */
export interface PasskeyCredential {
  id: Uuid;
  friendly_name: string | null;
  transports: string[] | null;
  created_at: string;
  last_used_at: string | null;
  is_current: boolean;
}

export interface DeviceLinkMint {
  /** Open this on the NEW device — shown as a copyable link and a QR code. */
  add_device_url: string;
  expires_in: number;
}

export const passkeyService = {
  /**
   * Mint an add-a-device link for yourself. No email round-trip: the caller is
   * already holding a live session, so their identity is proven. Same
   * short-lived single-use grant the emailed flow issues.
   */
  mintDeviceLink(client: ApiClient) {
    return client.request<DeviceLinkMint>(
      "/identity/passkey/device-link/mint",
      { method: "POST" },
    );
  },

  listCredentials(client: ApiClient) {
    return client.request<PasskeyCredential[]>("/identity/passkey/credentials");
  },

  /**
   * Remove a device. The server refuses the LAST credential (409) — passkeys
   * are the only way into a client account, so deleting the final one would
   * lock the user out with no recovery path.
   */
  deleteCredential(client: ApiClient, credentialId: Uuid) {
    return client.request<void>(
      `/identity/passkey/credentials/${credentialId}`,
      { method: "DELETE" },
    );
  },
};
